'use client'

import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Tab } from '@/components/widget/addable-tab-group'
import { resolveTabEntryTimeframe } from '@/features/chart/lib/tab-entry-timeframe'
import {
  getTabExpirationMs,
  pruneExpiredWatchListTabs,
  stripWatchList,
  useDataManagerSync,
} from '@/features/dashboard/hooks/use-data-manager-sync'
import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import {
  useLinkingColor,
  useLinkingGroupActions,
  useLinkingGroupWatchList,
} from '@/hooks/use-linking-group-state'
import { track } from '@/lib/analytics'
import { SUPPORTED_EXCHANGE } from '@/lib/configs/exchange-config'
import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { toExchangeEnum } from '@/lib/proto/util'
import {
  accountsForVenue,
  isOrderEligibleAccount,
  resolveDefaultAccountId,
} from '@/lib/utils/account-select'
import { formatSymbol } from '@/lib/utils/format-util'
import { useUserAccounts } from '@/service/account.hook'
import { watchListAtom } from '@/state/dashboard'
import { LinkingColor } from '@/state/linking'
import { chartWidgetScopeKey, resolveLegacyTabTimeframe } from '@/state/linking-group'
import { papertradeOneSecondEntryAtom, type WatchList } from '@/state/storage'
import { chartTabTimeframeAtomFamily } from '@/state/widget-instances'
import type { PublicAccount } from '@/types/generated/common/account'

export interface OpenInstrumentTabParams {
  symbol: string
  type: 'spot' | 'future' | 'perps' | 'options'
  exchange: keyof typeof SUPPORTED_EXCHANGE
  pricePrecision?: string
  tickSize?: string
  expiry?: string
  expirationTsMs?: string
  accountId?: string
  replaceCurrentTab?: boolean
  replaceAtIndex?: number
}

export type WatchListTabValue = string // id of watchlist item

/**
 * How long a row's single-click chart navigation waits for a possible second click.
 * A double click means "open in a NEW tab", so the single-click action (which replaces
 * the focused tab) has to be cancellable — but the whole window is dead time the user
 * feels as lag, so keep it as short as double-click detection tolerates. Shared by every
 * row list with this click/double-click pair (option table, watchlist, watch-list table)
 * so they can't drift out of sync.
 */
export const ROW_SINGLE_CLICK_DELAY_MS = 120

// Prune just after an expiry, not on it, so the wall clock has definitely passed it.
const EXPIRY_PRUNE_GRACE_MS = 500
// setTimeout overflows past ~24.8 days; a far expiry just re-arms when this fires.
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1

function normalizeTabSymbol(symbol: string) {
  return symbol.trim().toUpperCase()
}

export function getWatchListItemMatchKey(item: {
  symbol: string
  type: 'spot' | 'future' | 'perps' | 'options'
  exchange: keyof typeof SUPPORTED_EXCHANGE
}) {
  return `${item.exchange}:${normalizeTabSymbol(item.symbol)}`
}

export function getOpenInstrumentMatchKey(params: {
  symbol: string
  type: 'spot' | 'future' | 'perps' | 'options'
  exchange: keyof typeof SUPPORTED_EXCHANGE
}) {
  return `${params.exchange}:${normalizeTabSymbol(params.symbol)}`
}

function getFocusedTabId(list: WatchList['list'], selectedIndex: number) {
  if (selectedIndex < 0 || selectedIndex >= list.length) return null
  return list[selectedIndex]?.id ?? null
}

export function withFocusedIndex(list: WatchList['list'], selectedIndex: number): WatchList {
  return {
    list,
    focusedTabId: getFocusedTabId(list, selectedIndex),
    selectedIndex,
  }
}

export function reorderWatchListByIds(watchList: WatchList, nextIds: string[]): WatchList {
  if (!watchList.list.length) return watchList

  const idToItem = new Map(watchList.list.map((item) => [item.id, item]))
  const reorderedList = nextIds
    .map((id) => idToItem.get(id))
    .filter((item): item is WatchList['list'][number] => Boolean(item))

  if (reorderedList.length !== watchList.list.length) {
    const nextIdSet = new Set(nextIds)
    watchList.list.forEach((item) => {
      if (!nextIdSet.has(item.id)) {
        reorderedList.push(item)
      }
    })
  }

  const selectedId = watchList.list[watchList.selectedIndex]?.id
  const nextSelectedIndex = selectedId
    ? reorderedList.findIndex((item) => item.id === selectedId)
    : -1

  return withFocusedIndex(reorderedList, nextSelectedIndex)
}

/** A fresh tab item for `params`, with its own seeded account. */
export function createInstrumentTabItem(
  params: OpenInstrumentTabParams,
  accounts: PublicAccount[]
): WatchList['list'][number] {
  // Seed the new tab's own account: the venue's single-or-stable-default
  // order-eligible account for this tab's exchange, not the globally-selected
  // account. Order-eligible (not just ACTIVE), so a READ_ONLY account is never
  // seeded as this tab's trading identity — matches every other order-target
  // resolution (see isOrderEligibleAccount).
  const venueAccounts = accountsForVenue(
    accounts,
    exchangeToTradeVenue(toExchangeEnum(params.exchange))
  )
  const eligibleVenueAccounts = venueAccounts.filter(isOrderEligibleAccount)
  const seededAccountId = params.accountId ?? resolveDefaultAccountId(eligibleVenueAccounts)
  return {
    id: `${params.exchange}-${params.symbol}-${Date.now()}`,
    symbol: params.symbol,
    type: params.type,
    exchange: params.exchange,
    ...(seededAccountId ? { accountId: seededAccountId } : {}),
    ...(params.pricePrecision != null && { pricePrecision: params.pricePrecision }),
    ...(params.tickSize != null && { tickSize: params.tickSize }),
    ...(params.expiry != null && { expiry: params.expiry }),
    ...(params.expirationTsMs != null && { expirationTsMs: params.expirationTsMs }),
  }
}

/**
 * Hook for managing watchlist tabs state
 *
 * @param cardId - Optional card ID for linking group state. If provided, uses group-scoped state.
 *                 If not provided, falls back to global watchListAtom for backward compatibility.
 * @returns Watchlist tabs state and handlers
 */
export function useWatchlistTab(cardId?: string) {
  // Use linking group state if cardId is provided. Always called (never conditionally)
  // with a placeholder scope id when cardId is absent, so hook call order never changes
  // — the fallback to global state below is decided purely by which VALUE to use, not
  // by skipping any hook call. Narrow subscriptions only (watchList slice + write-only
  // actions + linking color) instead of the full useLinkingGroupState slice, since this
  // hook doesn't need selectedTicker/positionFilter/chartOption/etc.
  const scopeId = cardId ?? '__global_watchlist_tab__'
  const scopedWatchList = useLinkingGroupWatchList(scopeId)
  const { setWatchList: setScopedWatchList } = useLinkingGroupActions(scopeId)
  const scopedLinkingColor = useLinkingColor(scopeId)

  // Fallback to global state for backward compatibility
  const [globalWatchList, setGlobalWatchList] = useAtom(watchListAtom)
  const { data: userAccountsData } = useUserAccounts()

  // Select the appropriate state source
  const watchList = cardId ? scopedWatchList : globalWatchList
  const setWatchList = cardId ? setScopedWatchList : setGlobalWatchList
  const linkingColor: LinkingColor | undefined = cardId ? scopedLinkingColor : undefined

  // Per-widget tab timeframes — independent of the (color-)shared tab list
  // itself, so same-color chart widgets can each show a different timeframe
  // for the same focused ticker. See chartTabTimeframeAtomFamily for the
  // scoping/lazy-fallback rules; '__unscoped__' is never read/written when
  // cardId is absent (the no-cardId global fallback keeps writing timeframe
  // directly onto the shared tab item, same as before this change — there's
  // no cross-widget sharing concern for that singleton global watchlist).
  const pageId = useContext(LinkingPageContext)
  const chartScopeKey = cardId ? chartWidgetScopeKey(pageId, cardId) : '__unscoped__'
  const [tabTimeframes, setTabTimeframes] = useAtom(chartTabTimeframeAtomFamily(chartScopeKey))

  // Sync tab metadata to server only when structural tab data changes (list/selectedIndex),
  // not when price fields update. stripWatchList removes volatile price fields before comparing.
  const { syncTabs } = useDataManagerSync()
  const prevStrippedRef = useRef<string>('')
  useEffect(() => {
    const stripped = JSON.stringify(stripWatchList(watchList))
    if (stripped === prevStrippedRef.current) return
    prevStrippedRef.current = stripped
    if (cardId && linkingColor && linkingColor !== 'unlinked') {
      syncTabs(linkingColor, watchList)
    }
  }, [watchList, cardId, linkingColor, syncTabs])

  // Convert watchList.list to Tab format
  const tabs = useMemo<Tab<WatchListTabValue>[]>(() => {
    return watchList.list.map((item) => ({
      key: item.id,
      value: item.id,
      stableId: item.id,
      symbol: item.symbol,
      exchange: toExchangeEnum(item.exchange),
      isOption: item.type === 'options',
      children: (
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-semibold tracking-tight">
            {formatSymbol(item.symbol)}
          </p>
        </div>
      ),
    }))
  }, [watchList.list])

  const activeIndex = useMemo(() => {
    if (watchList.selectedIndex < 0 || watchList.selectedIndex >= watchList.list.length) {
      return -1
    }
    return watchList.selectedIndex
  }, [watchList.selectedIndex, watchList.list.length])

  const selectedSymbol = useMemo(() => {
    if (activeIndex < 0 || activeIndex >= watchList.list.length) {
      return null
    }
    return watchList.list[activeIndex]
  }, [activeIndex, watchList.list])

  const handleTabChange = (value: WatchListTabValue) => {
    const index = watchList.list.findIndex((item) => item.id === value)
    if (index !== -1 && index !== watchList.selectedIndex) {
      setWatchList({
        ...watchList,
        focusedTabId: watchList.list[index].id,
        selectedIndex: index,
      })
    }
  }

  // Ref, not a dep: openInstrumentTab's identity is a dependency of many callbacks
  // in the watchlist/data widgets, and must not change on every watchlist write.
  const watchListRef = useRef(watchList)
  watchListRef.current = watchList

  const openInstrumentTab = useCallback(
    (params: OpenInstrumentTabParams) => {
      // The one explicit "user opened an instrument" gesture (watchlist rows,
      // picker, data widget, option chain "open in chart"). Re-clicking the tab
      // that is already focused is a no-op below, so it isn't an open either.
      const focusedTab = watchListRef.current.list[watchListRef.current.selectedIndex]
      const isAlreadyFocused =
        params.replaceCurrentTab &&
        focusedTab &&
        getWatchListItemMatchKey(focusedTab) === getOpenInstrumentMatchKey(params)
      if (!isAlreadyFocused) {
        void track('Opened Instrument', {
          exchange: params.exchange.toLowerCase(),
          ticker: params.symbol,
        })
      }

      const newItem = createInstrumentTabItem(params, userAccountsData?.accounts ?? [])

      setWatchList((prev) => {
        if (params.replaceCurrentTab) {
          if (prev.list.length === 0) return withFocusedIndex([newItem], 0)

          const incomingKey = getOpenInstrumentMatchKey(params)
          const currentTab = prev.list[prev.selectedIndex]

          // Clicking the symbol that's already focused: no-op, don't churn a new id.
          if (currentTab && getWatchListItemMatchKey(currentTab) === incomingKey) {
            return prev
          }

          // Ticker changed, but this is still the same tab — keep its timeframe
          // instead of falling back to the chart's default.
          const replacementItem =
            currentTab?.timeframe != null
              ? { ...newItem, timeframe: currentTab.timeframe }
              : newItem
          const newList = prev.list.map((tab, i) =>
            i === prev.selectedIndex ? replacementItem : tab
          )
          return withFocusedIndex(newList, prev.selectedIndex)
        }

        if (params.replaceAtIndex !== undefined) {
          const oldItem = prev.list[params.replaceAtIndex]
          const replacementItem =
            oldItem?.timeframe != null ? { ...newItem, timeframe: oldItem.timeframe } : newItem
          const newList = [...prev.list]
          newList[params.replaceAtIndex] = replacementItem
          return withFocusedIndex(newList, params.replaceAtIndex)
        }

        // Explicit new-tab actions (double-click / "Open in Chart") land here.
        // Duplicates are allowed so the caller's command is followed exactly.
        return withFocusedIndex([...prev.list, newItem], prev.list.length)
      })
    },
    [userAccountsData?.accounts, setWatchList]
  )

  const removeTab = (index: number) => {
    if (index < 0 || index >= watchList.list.length) return

    const newList = watchList.list.filter((_, i) => i !== index)
    let newSelectedIndex = watchList.selectedIndex

    // Adjust selectedIndex if needed
    if (watchList.selectedIndex === index) {
      // If removing the selected tab, select the previous one or -1 if list is empty
      newSelectedIndex = newList.length > 0 ? Math.max(0, index - 1) : -1
    } else if (watchList.selectedIndex > index) {
      // If removing a tab before the selected one, decrement the index
      newSelectedIndex = watchList.selectedIndex - 1
    }

    setWatchList(() => withFocusedIndex(newList, newSelectedIndex))
  }

  const removeExpiredOptionTabs = useCallback(
    (referenceTimeMs: number) => {
      if (!Number.isFinite(referenceTimeMs) || referenceTimeMs <= 0) return

      setWatchList((prev) => {
        return pruneExpiredWatchListTabs(prev, referenceTimeMs)
      })
    },
    [setWatchList]
  )

  // Clean persisted tabs immediately on refresh using the local wall clock.
  // Chart rendering must never wait for the optional server-time request.
  useEffect(() => {
    removeExpiredOptionTabs(Date.now())
  }, [removeExpiredOptionTabs])

  // …and again the moment the next open tab expires, while the page stays open. Without it an
  // expired option stayed focused and the chart kept subscribing to an instrument the server
  // no longer knows. The prune keeps the usual rules: an expired focused tab hands focus to
  // its nearest live tab on the left (else the first one; none left → empty), and any other
  // expired tab just closes. Idempotent, so widgets sharing a tab list may each schedule it.
  const nextExpiryMs = useMemo(() => {
    const now = Date.now()
    let next: number | null = null
    for (const tab of watchList.list) {
      const expirationMs = getTabExpirationMs(tab)
      if (expirationMs !== null && expirationMs > now && (next === null || expirationMs < next)) {
        next = expirationMs
      }
    }
    return next
  }, [watchList.list])
  // Bumped when a capped timer fires before its expiry, so the effect re-arms.
  const [expiryTimerTick, setExpiryTimerTick] = useState(0)
  useEffect(() => {
    if (nextExpiryMs === null) return
    const delay = Math.min(
      Math.max(nextExpiryMs - Date.now(), 0) + EXPIRY_PRUNE_GRACE_MS,
      MAX_TIMER_DELAY_MS
    )
    const timer = setTimeout(() => {
      removeExpiredOptionTabs(Date.now())
      setExpiryTimerTick((n) => n + 1)
    }, delay)
    return () => clearTimeout(timer)
  }, [nextExpiryMs, expiryTimerTick, removeExpiredOptionTabs])

  const reorderTabs = useCallback(
    (nextTabs: Tab<WatchListTabValue>[]) => {
      setWatchList((prev) => {
        return reorderWatchListByIds(
          prev,
          nextTabs.map((tab) => tab.value)
        )
      })
    },
    [setWatchList]
  )

  // Depends on primitives derived from watchList rather than watchList.list itself, so
  // this callback's identity (and therefore any React.memo'd consumer, e.g. ChartWidget)
  // stays stable across a re-render that leaves the selected tab's id unchanged — even
  // though areWatchListsEqual already keeps `watchList` itself reference-stable in that
  // case, narrowing further here removes any dependency on watchList.list's identity.
  const hasValidSelection =
    watchList.selectedIndex >= 0 && watchList.selectedIndex < watchList.list.length
  const selectedTabId = watchList.list[watchList.selectedIndex]?.id

  const updateTabTimeframe = useCallback(
    (timeframe: number) => {
      if (!hasValidSelection) return

      if (!cardId) {
        // No linking-group scope — keep the legacy per-tab-field behavior.
        // There's no cross-widget sharing concern for the singleton global watchlist.
        setWatchList((prev) => ({
          ...prev,
          list: prev.list.map((item, i) =>
            i === prev.selectedIndex ? { ...item, timeframe } : item
          ),
        }))
        return
      }

      if (!selectedTabId) return
      setTabTimeframes((prev) => ({
        default: timeframe,
        perTab: { ...(prev?.perTab ?? {}), [selectedTabId]: timeframe },
      }))
    },
    [hasValidSelection, selectedTabId, cardId, setWatchList, setTabTimeframes]
  )

  // Resolved timeframe for the currently focused tab: this widget's own
  // per-tab override → this widget's last-used default → the legacy
  // shared-per-color value (one-time migration fallback) → hard default.
  const storedTimeframe = useMemo(() => {
    if (!selectedSymbol) return undefined
    if (!cardId || !linkingColor) return selectedSymbol.timeframe ?? 3600
    return (
      tabTimeframes?.perTab[selectedSymbol.id] ??
      tabTimeframes?.default ??
      resolveLegacyTabTimeframe(pageId, cardId, linkingColor, selectedSymbol.id) ??
      selectedSymbol.timeframe ??
      3600
    )
  }, [selectedSymbol, cardId, linkingColor, tabTimeframes, pageId])

  const previousTabId = useRef<string | undefined>(undefined)
  const papertradeOneSecondEntry = useAtomValue(papertradeOneSecondEntryAtom)
  const selectedExchange = selectedSymbol ? toExchangeEnum(selectedSymbol.exchange) : undefined
  const selectedTimeframe =
    selectedExchange === undefined
      ? storedTimeframe
      : resolveTabEntryTimeframe(
          selectedExchange,
          selectedTabId,
          previousTabId.current,
          storedTimeframe,
          papertradeOneSecondEntry
        )

  useEffect(() => {
    previousTabId.current = selectedTabId
    if (
      !selectedTabId ||
      selectedTimeframe === undefined ||
      selectedTimeframe === storedTimeframe
    ) {
      return
    }
    if (!cardId) {
      setWatchList((prev) => ({
        ...prev,
        list: prev.list.map((item) =>
          item.id === selectedTabId ? { ...item, timeframe: selectedTimeframe } : item
        ),
      }))
      return
    }
    // Automatic entry overrides only this tab, preserving the widget's last-used default.
    setTabTimeframes((prev) => ({
      default: prev?.default,
      perTab: { ...(prev?.perTab ?? {}), [selectedTabId]: selectedTimeframe },
    }))
  }, [selectedTabId, selectedTimeframe, storedTimeframe, cardId, setWatchList, setTabTimeframes])

  return {
    tabs,
    activeIndex,
    selectedSymbol,
    selectedTimeframe,
    handleTabChange,
    removeTab,
    removeExpiredOptionTabs,
    reorderTabs,
    updateTabTimeframe,
    openInstrumentTab,
    linkingColor, // Expose for components that need to know the group
  }
}
